// React.
import { useContext, useState } from 'react';

// Third-parties.
import { useToasts } from 'react-toast-notifications';

// Types.
import Filter from '../types/Filter';

// Contexts.
import { FilterContext } from '../context/FilterContext';
import { useTimeRange } from '../context/TimeRangeContext';
import { useUserRole } from '../context/UserRoleContext';
import { API_DATE_FORMAT } from '../helpers/timeRangeUtils';

// Helpers.
import { fetchFilters, getTimeZone, sendData } from '../helpers/utils';

// Utils.
import { showError } from '../utils/showAlert';

export default function FilterSaveButton ()
{
	const { filters, setFilters } = useContext (FilterContext);
	const timeRangeContext = useTimeRange();
	const { isAdmin, isDatasourceAdmin } = useUserRole();
    const [ name, setName ] = useState<string> ('');
    const [ description, setDescription ] = useState<string> ('');
    const [ keepData, setKeepData ] = useState<boolean> (false);
	const [ nameClass, setNameClass ] = useState<string> ('');
	const [ nameUsed, setNameUsed ] = useState<boolean> (false);

	const { addToast } = useToasts();

	function isDuplicateName (value: string): boolean
	{
		return filters.findIndex ((object: Filter) =>
			object.name.toLowerCase () === value.toLowerCase ()) >= 0;
	}

	function validateName (value: string)
	{
		// Trim any white space.
		const trimmedValue = value.trim ();

		if (trimmedValue === '')
		{
			setNameClass ('is-invalid');
			setNameUsed (false);
		}
		else if (isDuplicateName (trimmedValue))
		{
			setNameClass ('is-invalid');
			setNameUsed (true);
		}
		else
		{
			setNameClass ('is-valid');
			setNameUsed (false);
		}
	}

	function handleNameBlur ()
	{
		// Trim any white space.
		const value = name.trim ();

		// Save to state.
		setName (value);
		validateName (value);
	}

	function isValidForm (): boolean
	{
		return nameClass === 'is-valid';
	}

	async function saveFilterAndRefreshList (method: string, data: any)
	{
		try
		{
			await sendData (method, '/filter', data);
			setFilters (await fetchFilters ());

			// Reset fields ready for next time.
			setName ('');
			setDescription ('');
			setKeepData (false);
			setNameUsed (false);
			setNameClass ('');
			
			// Close the dropdown after successful save
			const dropdownToggle = document.getElementById('saveFilter');
			if (dropdownToggle) {
				// Trigger the dropdown toggle to close it
				dropdownToggle.click();
			}
			
			addToast('Snapshot saved', { appearance: 'info', autoDismiss: true });
		}
		catch (x: any)
		{
			addToast('Snapshot did not save', { appearance: 'warning', autoDismiss: true });
			console.error (x);
			const errorMessage = x.response?.data?.message || x.message || x.toString();
			showError('Failed to save snapshot', errorMessage);
		}
	}

	async function saveFilter ()
	{
		// Validate the form.
		if (!isValidForm ())
		{
			addToast('Snapshot did not save', { appearance: 'warning', autoDismiss: true });
			return;
		}

		const parameters: string =
			'?from=' + timeRangeContext.timeRange.from.format(API_DATE_FORMAT) +
			'&to=' + timeRangeContext.timeRange.to.format(API_DATE_FORMAT) +
			'&tz=' + getTimeZone () +
			'&interval=' + timeRangeContext.timeRange.interval;

		const data =
		[{
			id: 0,
			name,
			parameters,
			keepdata: keepData,
			description
		}];
		console.log(parameters)
		saveFilterAndRefreshList ('POST', data);
	}

	if (!isAdmin && !isDatasourceAdmin)
	{
		return null;
	}

    return (<>
		<button id="saveFilter" type="button" className="btn btn btn-dropdown dropdown-toggle btn-heading" data-bs-toggle="dropdown" aria-expanded="false" data-class="text-center" data-tooltip-id="global-tooltip" data-tooltip-content="Save snapshot">
			<i className="fal fa-camera-alt fa-fw" aria-hidden="true"></i>
		</button>
		<div id="saveFilterDropdown" className="dropdown-menu filter-dropdown-menu" aria-labelledby="saveFilter" >
			<div className="row" onClick={(e) => e.stopPropagation ()}>
				<div className="card-header">
					<i className="fal fa-camera-alt fa-fw mr-10" aria-hidden="true"></i>
					Save snapshot
				</div>
				<div className="card-body">
					<p>Please complete the required details below:</p>

					<div className="row g-0 form">

						{/* Name */}
						<div className="col-xs-12 col-md-3">
							<span className="required"><label htmlFor="name" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Snapshot name">Name</label></span>
						</div>
						<div className="col-xs-12 col-md-9 col-lg-9">
							<input
								type="text"
								id="name"
								autoFocus
								className={`form-control ${nameClass}`}
								required
								maxLength={250}
								onBlur={handleNameBlur}
								onChange={e => {
									setName (e.target.value);
									validateName (e.target.value);
								}}
								value={name}
								disabled={false}
								data-lpignore={true}
							/>
							{nameUsed && (
								<small className="text-danger">Snapshot name already used.</small>
							)}
						</div>
						<div className="w-100"></div>

						{/* Description */}
						<div className="col-xs-12 col-md-3">
							<label htmlFor="name" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Description">Description</label>
						</div>
						<div className="col-xs-12 col-md-9 col-lg-9">
							<input
								type="text"
								id="description"
								className={`form-control`}
								maxLength={250}
								onChange={e => setDescription (e.target.value)}
								value={description}
								disabled={false}
								data-lpignore={true}
							/>
						</div>
						<div className="w-100"></div>

						{/* Keep Data */}
						<div className="col-xs-12 col-md-3">
							<label htmlFor="keepdata" className="reminder" data-tooltip-id="global-tooltip" data-tooltip-content="Keep data">Keep Data</label>
						</div>
						<div className="col-xs-12 col-md-9 col-lg-9">
							<div className="form-check form-switch">
								<input
									type="checkbox"
									id="keepdata"
									className="form-check-input"
									onChange={() => setKeepData (!keepData)}
									checked={keepData}
									disabled={false}
									data-lpignore={true}
								/>
							</div>
						</div>
						<div className="w-100"></div>


					</div>
				</div>
			</div>
			<div className="row g-0 form">
				{/* Actions */}
				<div className="col-xs-12 col-md-12 col-lg-12 actions p-2">
					<button type="button" className="btn btn-secondary" data-bs-dismiss="dropdown"><i className="fal fa-times-circle fa-fw fa-sm"></i><span>Cancel</span></button>

					<button 
						className="btn btn-success" 
						onClick={(e) => {
							e.stopPropagation();
							saveFilter();
						}}
						disabled={!isValidForm()}
					><i className="fal fa-save fa-fw fa-sm"></i><span>Create</span></button>
				</div>
		</div>
		</div>
    </>);
}
